import CloseIcon from "@mui/icons-material/Close";

import { type GoalState } from "../buildRequestTemplate";

import { CreateEventsGoalForm } from "./CreateEventsGoalForm";
import { DatasetSummaryGoalForm } from "./DatasetSummaryGoalForm";
import { DatasetTriageGoalForm } from "./DatasetTriageGoalForm";
import styles from "./GoalCard.module.css";
import {
  type CreateEventsGoalErrors,
  type SummaryGoalErrors,
  type TriageGoalErrors,
  validateGoal,
} from "./validation";

interface GoalCardProps {
  goal: GoalState;
  index: number;
  /** When false, the X-close button is hidden and edit-only affordances
   *  inside the type-specific form are suppressed. Form controls
   *  themselves are disabled by the surrounding ``<fieldset>`` in
   *  ``AgentEditor``. */
  isEditing: boolean;
  onChange: (next: GoalState) => void;
  onRemove: () => void;
}

const GOAL_TYPE_LABEL: Record<GoalState["goal_type"], string> = {
  dataset_summary: "Dataset summary",
  dataset_triage: "Dataset triage",
  create_events: "Create events",
};

type GoalErrors = SummaryGoalErrors | TriageGoalErrors | CreateEventsGoalErrors;

/** Narrows the union returned by ``validateGoal`` so the dispatch below
 *  can pass errors to each form without a cast. The discriminant on
 *  ``GoalState`` isn't structurally tied to the error type, so TypeScript
 *  can't correlate them on its own. */
function isSummaryErrors(errors: GoalErrors): errors is SummaryGoalErrors {
  return "summary_format_spec_prompt" in errors;
}

function isTriageErrors(errors: GoalErrors): errors is TriageGoalErrors {
  return "rows" in errors;
}

function isCreateEventsErrors(
  errors: GoalErrors,
): errors is CreateEventsGoalErrors {
  return "event_focus_prompt" in errors;
}

/**
 * Wraps a single goal entry with a header (type label + remove button)
 * and dispatches to the type-specific form. Validation runs locally so
 * the form can render inline error chips without round-tripping through
 * the parent — the parent uses ``allGoalsValid`` from the same module
 * for its submit-disabled check.
 */
export function GoalCard({
  goal,
  index,
  isEditing,
  onChange,
  onRemove,
}: GoalCardProps) {
  const humanIndex = index + 1;
  const typeLabel = GOAL_TYPE_LABEL[goal.goal_type];
  const errors = validateGoal(goal);
  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <span className={styles.indexBadge}>{humanIndex}</span>
          <span className={styles.title}>{typeLabel}</span>
        </div>
        {isEditing && (
          <button
            type="button"
            aria-label={`Remove goal ${humanIndex} (${typeLabel})`}
            className={styles.removeButton}
            onClick={onRemove}
          >
            <CloseIcon fontSize="small" />
          </button>
        )}
      </header>
      <div className={styles.body}>
        {goal.goal_type === "dataset_summary" && isSummaryErrors(errors) && (
          <DatasetSummaryGoalForm
            goal={goal}
            errors={errors}
            onChange={onChange}
          />
        )}
        {goal.goal_type === "dataset_triage" && isTriageErrors(errors) && (
          <DatasetTriageGoalForm
            goal={goal}
            errors={errors}
            onChange={onChange}
            isEditing={isEditing}
          />
        )}
        {goal.goal_type === "create_events" && isCreateEventsErrors(errors) && (
          <CreateEventsGoalForm
            goal={goal}
            errors={errors}
            onChange={onChange}
            isEditing={isEditing}
          />
        )}
      </div>
    </section>
  );
}
